// Cleanup for the two bugs that already wrote bad data.
//
// Fixing the code stops new damage; it does not undo what is in KV. Both of
// these are invisible from the site: a boolean flag simply reads as not-live
// forever, and a leaked password sits in a record that `getall` serves to
// anyone unauthenticated.

import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import { useAdmin } from './AdminStore';
import { deleteFields, describeError, getAllRaw, patchVideo } from './api';
import { findBrokenFlags, findLeakedPasswords } from './schema';
import { readBool, writeBool } from '../ps/format';

export function RepairDialog({ open, onClose }) {
  const { refresh, toast } = useAdmin();
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setScan(null);
    setError('');
    // Deliberately re-reads raw: the store's copy is sanitized, so a leaked
    // password is invisible there by design.
    getAllRaw()
      .then((raw) => setScan({ flags: findBrokenFlags(raw), passwords: findLeakedPasswords(raw) }))
      .catch((err) => setError(describeError(err, 'Nothing was scanned.')));
  }, [open]);

  const repair = async () => {
    setBusy(true);
    setError('');
    try {
      for (const { identifier, keys, values } of scan.flags) {
        // Preserve the intent: a stored `true` meant live, it just could not
        // be read as such.
        const patch = Object.fromEntries(keys.map((key) => [key, writeBool(readBool(values[key]))]));
        await patchVideo(identifier, patch);
      }
      for (const { identifier } of scan.passwords) {
        await deleteFields(identifier, ['password']);
      }
      await refresh();
      toast('Repairs applied.', 'success');
      onClose();
    } catch (err) {
      setError(describeError(err, 'Some records may not have been repaired.'));
    } finally {
      setBusy(false);
    }
  };

  const nothingToDo = scan && !scan.flags.length && !scan.passwords.length;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Repair records</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: '10px' }}>{error}</Alert>}

        {!scan && !error && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', py: '10px' }}>
            <CircularProgress size={18} />
            <Typography variant="body2" sx={{ color: 'var(--ps-text-dim)' }}>
              Scanning every record…
            </Typography>
          </Box>
        )}

        {nothingToDo && <Alert severity="success">Nothing to repair.</Alert>}

        {scan?.flags.length > 0 && (
          <Alert severity="warning" sx={{ mb: '10px' }}>
            <AlertTitle>{scan.flags.length} invisible on the site</AlertTitle>
            These have “live” or “public” stored as a real boolean. The home page compares
            those fields against the string “true”, so they never match — the video stays
            hidden no matter what the admin showed.
            <List dense disablePadding sx={{ mt: '6px' }}>
              {scan.flags.map((row) => (
                <ListItem key={row.identifier} disableGutters sx={{ py: 0 }}>
                  <Typography variant="body2">
                    {row.title} <span className="admin-mono">({row.keys.join(', ')})</span>
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        {scan?.passwords.length > 0 && (
          <Alert severity="error">
            <AlertTitle>{scan.passwords.length} carrying the admin password</AlertTitle>
            The preview player used to hand its whole request body back as if it were the
            record, so the password got saved into it. The catalog endpoint is
            unauthenticated, so these are readable by anyone. Repairing deletes the field —
            consider changing the password too.
            <List dense disablePadding sx={{ mt: '6px' }}>
              {scan.passwords.map((row) => (
                <ListItem key={row.identifier} disableGutters sx={{ py: 0 }}>
                  <Typography variant="body2">{row.title}</Typography>
                </ListItem>
              ))}
            </List>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
        {!nothingToDo && (
          <Button variant="contained" onClick={repair} disabled={!scan || busy}>
            {busy ? 'Repairing…' : 'Repair all'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default RepairDialog;
